/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import pick from 'lodash/pick';
import map from 'lodash/map';
import filter from 'lodash/filter';
import { pipe } from 'ramda';
import { t } from '../../../../../../modules/infra/i18n';
import {
  prepareScheduleForSaving,
  prepareDaysForSaving,
  prepareMinsForSaving,
  getSchedulesValidationErrors,
  getDaysValidationErrors,
} from '../../../../../common/helpers/schedules';
import { trimFunction } from '../../../../../common/trimData';
import { barberPhoneOptional, barberEmailOptional, daysOffOptional } from '../../../../../common/helpers/modelSettings';
import Options from './Options';
import Permissions from './Permissions';
import Profile from '../containers/Profile';
import Appointments from './Appointments';
import Schedule from '../containers/Schedule';
import Notifications from './Notifications';
import Services from './Services';
import { USER_TYPES } from '../../../../../auth/constants';
import {
  canUserViewProfile,
  canUserModifyOptions,
  canUserModifyProfile,
  canUserModifyBarbers,
  canUserModifySchedules,
  canUserViewSchedules,
} from '../../../../../common/helpers/users';
import TabbedForm from '../../../../../common/TabbedForm';
import { FORM, PROFILE_TAB, SCHEDULE_TAB } from '../constants';
import { validate } from '../helpers';
import CancelButtonLink from '../../../../../common/buttons/cancelButtonLink';
import { containerApp } from '../../../../../../main';
import { BarberPayments } from '../../../../../../modules/shared/barbers/barber-form/barber-payments';
import { flagNames } from '../../../../../../modules/infra/feature-flags';

const fields = [
  'id',
  'avatar',
  'firstName',
  'lastName',
  'email',
  'phone',
  'instagram',
  'color',
  'tipEnabled',
  'earlyTipping',
  'lateTipping',
  'canCustomerCancel',
  'bookNoPay',
  'allowMultipleServices',
  'notifications',
  'schedules',
  'permissions',
  'days_off',
  'days_on',
  'advanceBookDays',
  'advanceBookMins',
  'advanceCancelMins',
  'minServiceLen',
  'bookingInterval',
  'requirePasscode',
  'passcode',
  'enabled',
  'defaultServiceId',
  'personnelId',
  'createUser',
  'username',
  'userEnabled',
  'customerReminder',
  'trackEmployeeTime',
  'hourlyWage',
  'managerHourlyWage',
  'meetingHourlyWage',
  'timeTrackRoles',
  'shopId',
  'services',
];

const pickRequiredFields = (data) => pick(data, fields);
export const prepareModel = pipe(pickRequiredFields, barberEmailOptional, barberPhoneOptional, daysOffOptional);

const isAdminOrShop = (user) =>
  user.kind === USER_TYPES.admin || user.kind === USER_TYPES.regularAdmin || user.kind === USER_TYPES.shop;

class BarberForm extends Component {
  save = async (data) => {
    const model = prepareModel(data);

    const schedulesErrors = getSchedulesValidationErrors(model.schedules);
    if (
      schedulesErrors.currentScheduleError ||
      schedulesErrors.futureScheduleError ||
      getDaysValidationErrors(model.days_off) ||
      getDaysValidationErrors(model.days_on)
    ) {
      return Promise.resolve();
    }

    model.permissions = model.permissions || {};
    if (data.populateUsername) {
      model.username = model.email;
    }

    if (this.props.useShopServices) {
      const services = filter(data.services, (service) => service.enabled);
      model.services = map(services, (service) => ({ id: service.id }));
    }

    if (!model.id) {
      model.days_off = prepareDaysForSaving(model.days_off);
    }

    model.schedules = prepareScheduleForSaving(model.schedules);
    model.days_on = prepareDaysForSaving(model.days_on);
    model.advanceBookMins = prepareMinsForSaving(model.advanceBookMins);
    model.advanceCancelMins = prepareMinsForSaving(model.advanceCancelMins);
    model.notifications.reminder.time = prepareMinsForSaving(model.notifications.reminder.time);
    model.customerReminder.barber_reminder.time = prepareMinsForSaving(data.customerReminder.barber_reminder.time);
    model.customerReminder.reminder.time = prepareMinsForSaving(data.customerReminder.reminder.time);

    return this.props.saveBarber(model);
  };

  renderDeleteButton = () => {
    const canDeleteBarber =
      this.props.deleteBarber && isAdminOrShop(this.props.user) && canUserModifyBarbers(this.props.permissions);

    return (
      <button
        display-if={canDeleteBarber}
        type="button"
        className="c-btn c-btn--red u-width-120 u-mr- u-float-left"
        onClick={this.props.deleteBarber}
      >
        <span className="c-btn__text">{t('buttons.delete')}</span>
      </button>
    );
  };

  renderCancelButton = () => (
    <CancelButtonLink
      link="/barbers"
      content={t('buttons.cancel')}
      display-if={isAdminOrShop(this.props.user)}
    />
  );

  renderSaveButton = (pristine, submitting, { active }, saveButtonText) => {
    const shouldSubmitBeDisplayed =
      canUserModifyBarbers(this.props.permissions) &&
      ([PROFILE_TAB, SCHEDULE_TAB].indexOf(active) === -1 ||
        (active === PROFILE_TAB && canUserModifyProfile(this.props.permissions)) ||
        (active === SCHEDULE_TAB && canUserModifySchedules(this.props.permissions)));
    return (
      <button
        display-if={shouldSubmitBeDisplayed}
        disabled={submitting}
        type="submit"
        className="c-btn u-min-width-125 pointer-events"
      >
        <span className="c-btn__text">{saveButtonText || t('buttons.save')}</span>
      </button>
    );
  };

  render() {
    const { permissions, user, initialValues } = this.props;

    return (
      <TabbedForm
        className="u-margin-auto u-color-white u-max-width-1000"
        form={FORM}
        initialValues={initialValues}
        validate={validate}
        onSubmit={trimFunction(this.save)}
        cancelButton={this.renderCancelButton}
        deleteButton={this.renderDeleteButton}
        saveButton={this.renderSaveButton}
        edit={this.props.edit}
        AddFormComponent={this.props.AddFormComponent}
      >
        <TabbedForm.Tab title={t('barber:form.profile_tab')}>
          <Profile {...this.props} />
        </TabbedForm.Tab>
        <TabbedForm.Tab title={t('barber:form.services_tab')} display-if={canUserViewProfile(permissions)} deny={USER_TYPES.barber}>
          <Services {...this.props} />
        </TabbedForm.Tab>
        <TabbedForm.Tab title={t('barber:form.notifications_tab')}>
          <Notifications {...this.props} />
        </TabbedForm.Tab>
        <TabbedForm.Tab title={t('barber:form.schedule_tab')} display-if={canUserViewSchedules(permissions)}>
          <Schedule {...this.props} />
        </TabbedForm.Tab>
        <TabbedForm.Tab title={t('barber:form.permissions_tab')} deny={USER_TYPES.barber} display-if={canUserViewProfile(permissions)}>
          <Permissions {...this.props} />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          title={t('barber:form.options_tab')}
          display-if={
            canUserViewProfile(permissions) && canUserModifyOptions(user) && canUserModifyProfile(permissions)
          }
        >
          <Options {...this.props} />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          title={t('barber:form.payments_tab')}
          deny={USER_TYPES.barber}
          display-if={this.props[flagNames.HYBRID_STAFF_PAYMENTS_TAB]}
        >
          <BarberPayments />
        </TabbedForm.Tab>
        <TabbedForm.Tab
          title={t('barber:form.appointments_tab')}
          display-if={!containerApp.isActive() && canUserViewProfile(permissions) && this.props.edit}
        >
          <Appointments {...this.props} />
        </TabbedForm.Tab>
      </TabbedForm>
    );
  }
}

BarberForm.propTypes = {
  useShopServices: PropTypes.bool.isRequired,
  saveBarber: PropTypes.func.isRequired,
  permissions: PropTypes.object.isRequired,
  disableUsername: PropTypes.bool, // eslint-disable-line react/no-unused-prop-types
  currentEmailValue: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
  schedules: PropTypes.object, // eslint-disable-line react/no-unused-prop-types
  user: PropTypes.object.isRequired,
  initialValues: PropTypes.object.isRequired,
  edit: PropTypes.bool,
  deleteBarber: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  showUsername: PropTypes.bool,
  isUserEnabled: PropTypes.bool,
  AddFormComponent: PropTypes.elementType.isRequired,
};

BarberForm.defaultProps = {
  disableUsername: false,
  showUsername: false,
  currentEmailValue: '',
  isUserEnabled: false,
  schedules: null,
  edit: false,
};

export default BarberForm;
